import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { frontloadConnect } from "react-frontload";

import pageLoader from "../../../../app/layouts/pages/loader";
import { exists, getKey, isObjectEmpty } from "../../../utils/helpers";
import * as pageType from "../../../constants/pageType";
import {
  getGeoRule,
  isCountryDetected,
  isGeoBlockedPath,
} from "../../../constants/geoBlocked";
import { fetchCurrentCountry } from "../../../../server/api/fetchApi";
import { getVanityRedirectURL } from "../../../utils/helpers/vanitiesURL";
import { setGeoCountryLocation } from "../../../../store/actions/country";
import { getCurrentContent } from "../../../../store/actions/content";
import { getEnvContent } from "../../../../store/utils/helpers";
import OrNewsletter from "../../../components/OrNewsletter";
import { OrDocumentariesPopup } from "../../../components/OrDocumentariesPopup";

const frontload = async props => {
  const { slug, brand, category } = props.match.params;

  if (slug && brand) {
    return props.getCurrentContent(slug, true, {
      brand,
      _format: "json",
    });
  } else if (slug && category) {
    return props.getCurrentContent(`medios/${slug}`, true, {
      category,
      _format: "json",
    });
  }

  return await props.getCurrentContent(slug || "home");
};

const Gateway = React.memo(
  ({ currentContent, location, country, setGeoCountryLocation }) => {
    const { type } = currentContent || {};
    const pathname = location && location.pathname;

    // External redirects (config.vanitiesURL) on client-side navigation;
    // direct hits are already redirected with a 301 by the server loader.
    const redirectURL = getVanityRedirectURL(pathname);
    React.useEffect(() => {
      if (redirectURL) {
        window.location.replace(redirectURL);
      }
    }, [redirectURL]);

    // Detect the visitor country through the ipService (MaxMind) only
    // when the current path has a geo-blocking rule. Client-side only:
    // during SSR the service would geolocate the server IP, not the user.
    React.useEffect(() => {
      if (!redirectURL && getGeoRule(pathname) && !isCountryDetected(country)) {
        fetchCurrentCountry().then(data => {
          const isoCode = getKey(data, "country.iso_code");
          setGeoCountryLocation(isoCode || "UNKNOWN");
        });
      }
    }, [pathname, country, redirectURL, setGeoCountryLocation]);

    if (redirectURL) {
      return null;
    }

    if (type && !exists(pageLoader[type])) {
      currentContent.error = {
        stack: 'El tipo de página "' + type + '" no existe',
      };
    }

    if (isGeoBlockedPath(pathname, country)) {
      const GeoBlockedPage = pageLoader[pageType.GEO_BLOCKED];
      return <GeoBlockedPage />;
    }

    const isNotFound =
      type === undefined ||
      currentContent.error !== undefined ||
      isObjectEmpty(currentContent);
    const LoadablePage = pageLoader[isNotFound ? pageType.NOTFOUND : type];

    // Adding Newsletter Campaign Data
    const newsletterCampaign = getKey(currentContent, "newsletter_campaign");
    return (
      <>
        <LoadablePage currentContent={currentContent} location={{ location }} />
        {newsletterCampaign && <OrNewsletter {...newsletterCampaign} />}
        {<OrDocumentariesPopup slug={currentContent.slug} />}
      </>
    );
  },
  (prevProps, nextProps) => {
    if (nextProps.country !== prevProps.country) {
      return false;
    }
    if (
      nextProps.match.params.slug !== prevProps.match.params.slug ||
      nextProps.currentContent.id !== prevProps.currentContent.id
    ) {
      nextProps.getCurrentContent(nextProps.match.params.slug || "home");
      return getEnvContent() === "production";
    }
    return getEnvContent() === "local";
  },
);

const mapStateToProps = state => ({
  currentContent: state.content.currentContent,
  country: state.country.country,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ getCurrentContent, setGeoCountryLocation }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  frontloadConnect(frontload, {
    noServerRender: false,
    onMount: true,
    onUpdate: false,
  })(Gateway),
);
